import * as _ from 'lodash';
import { groupBy } from '../helpers/groupBy';

import {
  Portfolio,
  PortfolioKey,
  Strategy
} from '../models/Recommendation/RecommendationInterfaces';

const assetBreakdownMap = new Map([
  ['Cash', { name: 'Cash', color: '#00A17D', className: 'chart-green' }],
  ['International Bonds', { name: 'Intl Bonds', color: '#7969CB', className: 'chart-purple-2' }],
  ['International Stocks', { name: 'Intl Stocks', color: '#00A4DC', className: 'chart-blue-2' }],
  ['US Bonds', { name: 'U.S. Bonds', color: '#4D40A1', className: 'chart-purple-1' }],
  ['US Stocks', { name: 'U.S. Stocks', color: '#0074D7', className: 'chart-blue-1' }]
]);

// TODO: Hardcoded
// Users are not shown Explore Other Options
// If they are in an income strategy
const _incomeStrategies = [6, 9];

// TODO: Hardcoded
// Tiers used to determine what copy to show
const _isGrowthPortfolioTiers = [3, 4, 5];

/**
* Return a strategy's historical returns.
* Used by historical returns modal
* @param {PortfolioKey} portfolioKey
*/
const historicalReturns = (strategy: Strategy) => {
  return strategy.portfolios.map((y: Portfolio) => ({
    name: y.portfolioName.replace('Cash-Enhanced ', ''),
    data: y.historicValues.map(value => (
      [Date.parse(value.date), value.cumulativePercentChange]
    )),
    portfolioTierId: y.portfolioTierId
  }));
};

// todo
const isIncome = (strategyId: number): boolean => {
  return _incomeStrategies.includes(strategyId);
};

/**
* The asset-breakdown component displays different copy
* based on the portfolio strategy or portfolio tier.
* @param {Portfolio} portfolio
*/
const checkIsGrowth = (portfolioKey: PortfolioKey) => {
  if (_incomeStrategies.includes(portfolioKey.strategy.strategyId)) {
    return true;
  }
  return _isGrowthPortfolioTiers.includes(portfolioKey.portfolioTierId);
};

/**
* Return a single portfolio's asset breakdown
* Used by md-asset-breakdown/pie graph
* @param {PortfolioKey} portfolioKey
*/
const assetBreakdown = (portfolioKey: PortfolioKey) => {
  const assetSets = portfolioKey.strategy.portfolios
  .find(z => z.portfolioTierId === portfolioKey.portfolioTierId).assetSets;

  return {
    assetSets: assetSets.map((z) => ({
      name: assetBreakdownMap.get(z.name).name,
      color: assetBreakdownMap.get(z.name).color,
      y: z.weight,
      sortOrder: z.sortOrder
    })).sort((a, b) => (a.sortOrder - b.sortOrder)),
    isGrowth: checkIsGrowth(portfolioKey)
  };
};

/**
* Return a single portfolio's table allocations
* Used by md-portfolio-allocations-table
* @param {PortfolioKey} portfolioKey
*/
const tableAllocations = (portfolioKey: PortfolioKey) => {
  const assetSets = portfolioKey.strategy.portfolios
    .find(z => z.portfolioTierId === portfolioKey.portfolioTierId).assetSets;
  // The usual grouping is US Stocks, Intl Stocks, US Bonds, etc ...
  // They want it US Stocks+Intl Stocks as one group: Stocks
  // Same idea for bonds
  // groupName is provided by the response. We just rearrange the object
  // to utilize the grouping
  return Object.entries(groupBy(assetSets, 'groupName')).map((y) => ({
    groupName: y[0],
    sortOrder: y[1][0].groupSortOrder,
    holdings: _.orderBy(y[1].reduce((acc, curr) => { return acc.concat(curr.holdings); }, []),
      ['symbolWeight', holding => holding.description.toLowerCase()], ['desc', 'asc'])
  })).sort((a, b) => (a.sortOrder - b.sortOrder));
};

/**
* Return a single portfolio's name, description, and other misc info
* Used by md-portfolio-summary
* @param {PortfolioKey} portfolioKey
*/
const portfolioSummary = (portfolioKey: PortfolioKey) => {
  const modelPortfolio = portfolioKey.strategy.portfolios
  .find((x: Portfolio) => x.portfolioTierId === portfolioKey.portfolioTierId);

  return {
    modelPortfolioId: modelPortfolio.modelPortfolioId,
    portfolioName: modelPortfolio.portfolioName,
    portfolioDescription: modelPortfolio.portfolioDescription,
    portfolioModelDescription: modelPortfolio.portfolioModelDescription,
    portfolioStrategyName: modelPortfolio.portfolioStrategyName,
    portfolioTierName: modelPortfolio.portfolioTierName,
    portfolioTierId: modelPortfolio.portfolioTierId
  };
};

/**
* Return true if portfolio is in range of recommended portfolio tier
* Used by explore-other-options to filter list down from 5 to 3 options
* @param {number} portfolioTierId
* @param {number} recommendedPortfolioTierId
*/
const filterExploreOtherOptions = (portfolioTierId: number, recommendedPortfolioTierId: number) => {
  if (recommendedPortfolioTierId === 1) {
    return (portfolioTierId <= 3);
  } else if (recommendedPortfolioTierId === 5) {
    return (portfolioTierId >= 3);
  } else if (recommendedPortfolioTierId > 1 && recommendedPortfolioTierId < 5) {
    return (portfolioTierId >= recommendedPortfolioTierId - 1 && portfolioTierId <= recommendedPortfolioTierId + 1);
  }
  return false;
};

/**
* Return multiple portfolios for quick comparison
* Used by explore-other-options route
* @param {PortfolioKey} portfolioKey
*/
const exploreOtherOptions = (portfolioKey: PortfolioKey) => {
  // cobble together an object with asset sets, best year, worst year, and portfolio id
  return portfolioKey.strategy.portfolios.filter(x => filterExploreOtherOptions(x.portfolioTierId,
    portfolioKey.recommendedPortfolioTierId))
    .map((y: Portfolio) => ({
      modelPortfolioId: y.modelPortfolioId,
      portfolioName: y.portfolioDescription,
      bestYear: y.bestYearReturn,
      worstYear: y.worstYearReturn,
      isRecommended: y.portfolioTierId === portfolioKey.recommendedPortfolioTierId,
      value: y.portfolioTierId, // Radio-card component binding
      portfolioTierId: y.portfolioTierId,
      portfolioTierName: y.portfolioTierName,
      assetSets: y.assetSets.map(z => ({
        name: assetBreakdownMap.get(z.name).name,
        color: assetBreakdownMap.get(z.name).color,
        className: assetBreakdownMap.get(z.name).className,
        y: z.weight,
        sortOrder: z.sortOrder
      })).sort((a, b) => (a.sortOrder - b.sortOrder))
    }));
};

/*
* Mappers
*/

/**
* Map API response to typed object for historical values
* @param {any[]} data
*/
const mapHistoricalValues = (data: Array<any>): Portfolio[] => {
  return data.map((x) => ({
    modelPortfolioId: x.OMAModelPortfolioId,
    portfolioName: x.PortfolioName,
    portfolioDescription: x.PortfolioDescription,
    portfolioModelDescription: x.ModelPortfolioDescription,
    portfolioStrategyId: x.PortfolioStrategy,
    portfolioStrategyName: x.PortfolioStrategyName,
    portfolioTierId: x.PortfolioTier,
    portfolioTierName: x.PortfolioTierName,
    stdDeviation: x.StdDeviation,
    annualizedReturn: x.AnnualizedReturn,
    cumulativeReturn: x.CumulativeReturn,
    averageReturn: x.AverageReturn,
    bestYearReturn: x.BestYearReturn,
    worstYearReturn: x.WorstYearReturn,
    historicValues: x.HistoricValues.map((y) => ({
      id: y.Id,
      portfolioId: y.PortfolioId,
      percentChange: y.PercentChange,
      cumulativePercentChange: y.CumulativePercentChange,
      date: y.Date
    })),
    calendarYearReturns: x.CalendarYearReturns.map((z) => ({
      id: z.Id,
      modelPortfolioValueTypeId: z.ModelPortfolioValueTypeId,
      portfolioId: z.OMAModelPortfolioId,
      date: z.Date,
      value: z.Value,
      valueTypeFixedName: z.ValueTypeFixedName
    }))
  }));
};

/**
* Map API response to typed object for asset sets/holdings
* @param {any[]} data
*/
const mapAssetSets = (data: Array<any>) => {
  return data.map((x) => ({
    modelPortfolioId: x.OMAModelPortfolioId,
    portfolioName: x.PortfolioName,
    portfolioDescription: x.PortfolioDescription,
    portfolioModelDescription: x.ModelPortfolioDescription,
    portfolioStrategyId: x.PortfolioStrategy,
    portfolioStrategyName: x.PortfolioStrategyName,
    portfolioTierId: x.PortfolioTier,
    portfolioTierName: x.PortfolioTierName,
    assetSets: x.PortfolioAssetSets.map((y) => ({
      groupName: y.AssetSetGroupName,
      name: y.AssetSetName,
      sortOrder: y.AssetSetSortOrder,
      groupSortOrder: y.AssetSetGroupSortOrder,
      weight: y.AssetSetWeight,
      holdings: y.Holdings.map((z) => ({
        description: z.Description,
        symbol: z.Symbol,
        symbolWeight: z.SymbolWeight
      }))
    }))
  }));
};


/**
 *  questionnaireTransformer
 *
 *  @fileOverview An object for transforming data into the appropriate format to be consumed by components
 *  @example
 *  QuestionnaireTransformer.mapAssetSets(assetSets.result.Result)
 *  QuestionnaireTransformer.historicalReturns(strategy)
 */
export const QuestionnaireTransformer = {
  historicalReturns,
  assetBreakdown,
  exploreOtherOptions,
  tableAllocations,
  portfolioSummary,
  mapHistoricalValues,
  mapAssetSets,
  isIncome,
  questionnaireUpdateFunctions: [
    { function: portfolioSummary, key: 'getPortfolioSummary' },
    { function: tableAllocations, key: 'getTableAllocations' },
    { function: assetBreakdown, key: 'getAssetBreakdown' },
    { function: exploreOtherOptions, key: 'getExploreOtherOptions' }
  ]
};
